<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    theme="blue-actions"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p
          class="kds-typography-title-xlarge"
          v-html="$t('eu__insurance_search__public__header')"
        />
        <CorporateHeader :corporate="corporate" />
        <p class="kds-typography-paragraph-large">
          {{ descriptionText }}
        </p>
      </div>

      <div
        v-if="showCorporateGuidancePrescriptionSection"
        v-html="corporateGuidanceDescriptionText"
      />

      <div class="kds-content-block">
        <p class="kds-typography-title-large">
          {{ onlineHeaderText }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ onlineDescriptionText }}
        </p>
      </div>

      <div class="kds-content-block">
        <p class="kds-typography-title-large">
          {{ postHeaderText }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ postDescriptionText }}
        </p>
        <div v-if="hasAddress">
          <p class="kds-typography-title-medium">
            {{ companyNameFromAddress }}
          </p>
          <p class="kds-typography-title-medium">
            {{ companyAddress }}
          </p>
        </div>
      </div>

      <div class="divider" />

      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ corporateInfoHeaderText }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ corporateInfoDescriptionText }}
        </p>

        <InsuranceExternalRedirect
          v-if="website"
          ref="websiteExternalRedirectRef"
          :cta="corporateInfoWebsiteCTAText"
          cta-view="outlined"
          @click="handleContactsCTAClick('website')"
        />
        <InsuranceExternalRedirect
          v-if="branchFinderUrl"
          ref="branchFinderUrlExternalRedirectRef"
          :cta="corporateInfoBranchCTAText"
          cta-view="outlined"
          @click="handleContactsCTAClick('branchFinderUrl')"
        />
        <BaseButton
          v-if="phone"
          :text="corporateInfoPhoneCTAText"
          variant="outlined"
          size="small"
          @click="handleContactsCTAClick('phone')"
        />
        <BaseModal
          ref="thirdPartyConfirmationModalRef"
          :title="$t('generic__third_party_url_confirmation__header')"
        >
          <template #footer>
            <div class="kds-content-block">
              <BaseButton
                :text="
                  $t('generic__third_party_url_confirmation__primary_label')
                "
                data-qa="submit"
                @click="handleThirdPartyConfirmationSubmit"
              />
              <BaseButton
                variant="outlined"
                :text="
                  $t('generic__third_party_url_confirmation__secondary_label')
                "
                data-qa="cancel"
                @click="handleThirdPartyConfirmationCancel"
              />
            </div>
          </template>
        </BaseModal>
      </div>
    </div>
    <template #actions>
      <CheckboxField
        v-model="activationConsent"
        data-qa="activation-consent"
      >
        <div v-html="$t('eu__prescription_insurance__primary_label')" />
      </CheckboxField>
      <BaseButton
        :disabled="activationBaseButtonState === 'DISABLED'"
        :text="$t('eu__prescription_insurance__primary_cta')"
        @click="handleActivationCTAClick"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import { useI18n } from 'petite-vue-i18n'
import InsuranceExternalRedirect from '@eu/app/components/InsuranceExternalRedirect.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import Tracker from '@shared/Tracker'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import CheckboxField from '@shared/components/form/CheckboxField.vue'
import BaseModal from '@shared/components/BaseModal.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import * as apiRequest from '@shared/api-endpoints/index.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'

const { t } = useI18n()
const corporateStore = useCorporateStore()
const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.PRESCRIPTION_INSURANCE,
)

const activationConsent = ref(false)
const activationBaseButtonState = computed(() =>
  activationConsent.value ? 'ACTIVE' : 'DISABLED',
)

const thirdPartyConfirmationModalRef = ref(null)
const nextThirdPartyUrlType = ref('')
const websiteExternalRedirectRef = ref(null)
const branchFinderUrlExternalRedirectRef = ref(null)

const corporate = computed(() => corporateStore.corporate)
const appName = computed(() => t('eu__generic__app'))

const descriptionText = computed(() =>
  t('eu__insurance_details__public__description_v9.3.0', {
    app_name: appName.value,
  }),
)
const onlineHeaderText = computed(() =>
  t('eu__insurance_details__public__online_header_v9.3.0'),
)
const onlineDescriptionText = computed(() =>
  t('eu__insurance_details__public__online_description_v9.3.0'),
)
const postHeaderText = computed(() =>
  t('eu__insurance_details__public__post_header_v9.3.0'),
)
const postDescriptionText = computed(() =>
  t('eu__insurance_details__public__post_description_v9.3.0'),
)
const corporateInfoHeaderText = computed(() =>
  t('eu__insurance_details__public__corporate_info_header_v9.3.0'),
)
const corporateInfoDescriptionText = computed(() =>
  t('eu__insurance_details__public__corporate_info_description_v9.3.0'),
)
const corporateInfoWebsiteCTAText = computed(() =>
  t('eu__insurance_details__public__corporate_info_website_v9.3.0'),
)
const corporateInfoBranchCTAText = computed(() =>
  t('eu__insurance_details__public__corporate_info_branch_v9.3.0'),
)
const corporateInfoPhoneCTAText = computed(() =>
  t('eu__insurance_details__public__corporate_info_phone_v9.3.0'),
)
const website = computed(() => corporate.value.primaryAddress?.website ?? '')
const branchFinderUrl = computed(
  () => corporate.value.primaryAddress?.branch_finder_url ?? '',
)
const phone = computed(() => corporate.value.primaryAddress?.phone ?? '')
const hasAddress = computed(
  () => companyNameFromAddress.value !== '' || companyAddress.value !== '',
)
const companyAddress = computed(() =>
  [
    corporate.value.primaryAddress?.address_line1,
    corporate.value.primaryAddress?.address_line2,
    corporate.value.primaryAddress?.zip,
    corporate.value.primaryAddress?.city,
  ]
    .filter((n) => n)
    .join(', '),
)
const companyNameFromAddress = computed(
  () => corporate.value.primaryAddress?.corporate_name ?? '',
)
const showCorporateGuidancePrescriptionSection = computed(
  () => corporate.value.prescriptionUploadMode === 'custom',
)
const corporateGuidanceDescriptionText = computed(() =>
  t('prescription_upload_instructions_text_key').replace(/[\n\t]/g, '<br />'),
)

const requestInvoiceByPhone = (phoneNumber) => {
  const link = document.createElement('a')
  link.setAttribute('href', `tel:${phoneNumber}`)
  link.click()
}

const openThirdPartyUrl = (urlType) => {
  thirdPartyConfirmationModalRef.value.open()
  nextThirdPartyUrlType.value = urlType
}

const handleContactsCTAClick = (type) => {
  let label = 'visit_website'
  switch (type) {
    case 'website':
      openThirdPartyUrl('website')
      break
    case 'branchFinderUrl':
      label = 'find_local_branch'
      openThirdPartyUrl('branchFinderUrl')
      break
    case 'phone':
      label = 'call_gkv'
      requestInvoiceByPhone(phone.value)
      break
    default:
      break
  }
  Tracker.trackButtonSelect(label, 'signup_insurance_info')
}

const handleActivationCTAClick = async () => {
  Tracker.trackButtonSelect('enter_diga_code', 'signup_insurance_info')
  apiRequest.sendSubscriptionActivationEntry({
    type: 'prescription_self_submitted',
  })
  // in-app users are returned into the app by the download-link guard
  await navigateNext()
}

const handleThirdPartyConfirmationSubmit = () => {
  thirdPartyConfirmationModalRef.value.close()
  let url = ''
  if (nextThirdPartyUrlType.value === 'website') {
    url = website.value
  } else if (nextThirdPartyUrlType.value === 'branchFinderUrl') {
    url = branchFinderUrl.value
  }
  url =
    url.indexOf('https://') === 0 || url.indexOf('http://') === 0
      ? url
      : `https://${url}`
  if (!url) {
    nextThirdPartyUrlType.value = ''
    return
  }
  if (nextThirdPartyUrlType.value === 'website') {
    websiteExternalRedirectRef.value.openUrl(url)
  } else if (nextThirdPartyUrlType.value === 'branchFinderUrl') {
    branchFinderUrlExternalRedirectRef.value.openUrl(url)
  }
  nextThirdPartyUrlType.value = ''
}

const handleThirdPartyConfirmationCancel = () => {
  thirdPartyConfirmationModalRef.value.close()
  nextThirdPartyUrlType.value = ''
}
</script>

<style lang="scss" scoped>
.logo {
  max-width: 100%;
  max-height: 100px;
}

.divider {
  width: 100%;
  border-bottom: 1px solid $kds-color-background-subtle;
}
</style>
